import { appApi } from "@src/integrations/redux/app-api";
import { HTTP_METHODS } from "@src/utils/network-constants";
import {
  urlForTimedShuttlesInstances,
  urlForTimedShuttleInstance,
  urlForTimedShuttlesDefinitions,
  urlForTimedShuttleDefinitionInstances,
  urlForTimedShuttleInstanceParticipants
} from "../timed-shuttles-api-urls";
import type {
  GetTimedShuttlesInstancesArgs,
} from "../timed-shuttles-dtos";
import type {
  AllTimedShuttleInstancesResponse,
} from "../timed-shuttles-models";


export interface AssignUserToRideRequest {
  spaceId: string;
  userId: string;
  rideId: string;
}

export interface AssignUserToRideResponse {
  ok: boolean;
  // backend may return more; keep flexible
  [key: string]: any;
}

export const timedShuttlesApi = appApi.injectEndpoints({
  // ① Declare the tag type
  tagTypes: ["TimedShuttleInstances"],
  endpoints: (builder) => ({
    // ② Provide the tag on the list query
    getTimedShuttlesInstances: builder.query<
      AllTimedShuttleInstancesResponse,
      GetTimedShuttlesInstancesArgs
    >({
      query: ({ spaceId, date }) => ({
        url: urlForTimedShuttlesInstances(spaceId, date),
        method: HTTP_METHODS.GET,
      }),
      providesTags: (_result, _error, { spaceId }) => [
        { type: "TimedShuttleInstances" as const, id: spaceId },
      ],
    }),
    createTimedShuttleInstance: builder.mutation<
      { rideId: string },
      CreateTimedShuttleInstanceArgs
    >({
      query: ({ spaceId, definitionId, date, hours, minutes }) => ({
        url: urlForTimedShuttleDefinitionInstances(spaceId),
        method: HTTP_METHODS.POST,
        body: { definitionId, date, hours, minutes},
      }),
      invalidatesTags: (_res, _err, { spaceId }) => [
        { type: "TimedShuttleInstances" as const, id: spaceId },
      ],
    }),

    // ③ Invalidate that tag after a delete
    deleteTimedShuttleInstance: builder.mutation<
      void,
      { spaceId: string; rideId: string }
    >({
      query: ({ spaceId, rideId }) => ({
        url: urlForTimedShuttleInstance(spaceId, rideId),
        method: HTTP_METHODS.DELETE,
      }),
      invalidatesTags: (_result, _error, { spaceId }) => [
        { type: "TimedShuttleInstances" as const, id: spaceId },
      ],
    }),
    getAllTimedShuttlesDefinitions: builder.query<AllShuttleDefinitions, GetAllShuttleDefinitionsArgs>({
			query: (args) => ({
				url: urlForTimedShuttlesDefinitions(args.spaceId),
				method: HTTP_METHODS.GET
			})
		}),
    bookTimedShuttleInstance: builder.mutation<
      { partId: string },
      { spaceId: string; rideId: string }
    >({
      query: ({ spaceId, rideId }) => ({
        url: urlForTimedShuttleInstanceParticipants(spaceId, rideId),
        method: HTTP_METHODS.POST,
      }),
      invalidatesTags: (_res, _err, { spaceId }) => [
        { type: "TimedShuttleInstances" as const, id: spaceId },
      ],
    }),

    removeTimedShuttleInstanceBooking: builder.mutation<
      void,
      { spaceId: string; rideId: string }
    >({
      query: ({ spaceId, rideId }) => ({
        url: urlForTimedShuttleInstanceParticipants(spaceId, rideId),
        method: HTTP_METHODS.DELETE,
      }),
      invalidatesTags: (_res, _err, { spaceId }) => [
        { type: "TimedShuttleInstances" as const, id: spaceId },
      ],
    }),
    assignDriverToTimedShuttleInstance: builder.mutation<
      { data: any },
      { spaceId: string; rideId: string; userId: string }
    >({
      query: ({ spaceId, rideId, userId }) => ({
        // Backend: PATCH /api/v1/<space_id>/timed-shuttle/instances/assign-driver
        url: `${urlForTimedShuttleInstance(spaceId, rideId)}/assign-driver`,
        method: HTTP_METHODS.PATCH,
        body: { rideId, userId },
      }),
      invalidatesTags: (_res, _err, { spaceId }) => [
        { type: "TimedShuttleInstances" as const, id: spaceId },
      ],
    }),
  }),
  overrideExisting: "throw",
});

export const {
  useGetTimedShuttlesInstancesQuery,
  useCreateTimedShuttleInstanceMutation,
  useDeleteTimedShuttleInstanceMutation,
  useGetAllTimedShuttlesDefinitionsQuery,
  useBookTimedShuttleInstanceMutation,
  useRemoveTimedShuttleInstanceBookingMutation,
  useAssignDriverToTimedShuttleInstanceMutation,
} = timedShuttlesApi;